// src/components/ProductModalForm.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Modal, Form, Input, Button, Col, Row, InputNumber, Select, Upload } from 'antd';
import { UploadOutlined, CloseCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { useCreateProduct, useUpdateProduct } from '../../mutations/products';
import { SEARCH_GROUPS } from '../../mutations/groups';
import { SEARCH_PRICE_SEGMENTATIONS, GET_ACTIVE_PRICE_SEGMENTATIONS } from '../../mutations/priceSegmentations';
import { SEARCH_PLACES, GET_ACTIVE_PLACES } from '../../mutations/places';
import { gql, useLazyQuery, useQuery } from '@apollo/client';
import './CustomForm.css';
import CurrencyInput from '../CurrencyInput';
import useDebounce from '../../hooks/useDebounce';

const ProductModalForm = ({ visible, onCancel, initialValues, isEditMode, refetchProducts }) => {
  const [createProduct] = useCreateProduct();
  const [updateProduct] = useUpdateProduct();
  const [searchGroups, { data: groupsData }] = useLazyQuery(SEARCH_GROUPS, {
    fetchPolicy: 'network-only'
  });
  const [searchPriceSegmentations, { data: priceSegmentationsData }] = useLazyQuery(SEARCH_PRICE_SEGMENTATIONS, {
    fetchPolicy: 'network-only'
  });
  const [searchPlaces, { data: placesData }] = useLazyQuery(SEARCH_PLACES, {
    fetchPolicy: 'network-only'
  });
  const [groups, setGroups] = useState([]);
  const [priceSegmentations, setPriceSegmentations] = useState([]);
  const [places, setPlaces] = useState([]);
  const { data: activePlacesData } = useQuery(GET_ACTIVE_PLACES, {
    fetchPolicy: 'network-only'
  });
  const { data: activePriceSegmentationsData } = useQuery(GET_ACTIVE_PRICE_SEGMENTATIONS, {
    fetchPolicy: 'network-only'
  });
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 1000);
  const [fileList, setFileList] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);
  const [form] = Form.useForm();

  useEffect(() => {
    if (debouncedSearchTerm.length > 2 || debouncedSearchTerm === '*') {
      searchGroups({ variables: { query: debouncedSearchTerm } });
    }
  }, [debouncedSearchTerm, searchGroups]);

  useEffect(() => {
    if (groupsData) {
      setGroups(groupsData.searchGroups);
    }
  }, [groupsData]);

  useEffect(() => {
    if (priceSegmentationsData) {
      setPriceSegmentations(priceSegmentationsData.searchPriceSegmentations);
    }
  }, [priceSegmentationsData]);

  useEffect(() => {
    if (placesData) {
      console.log('Places data from search:', placesData.searchPlaces);
      setPlaces(placesData.searchPlaces);
    }
  }, [placesData]);

  useEffect(() => {
    if (activePlacesData && activePlacesData.activePlaces) {
      console.log('Active places data:', activePlacesData.activePlaces);
      setPlaces(prevPlaces => {
        const uniquePlaces = [...prevPlaces];
        activePlacesData.activePlaces.forEach(place => {
          if (!uniquePlaces.some(p => p.id === place.id)) {
            uniquePlaces.push(place);
          }
        });
        return uniquePlaces;
      });
    }
  }, [activePlacesData]);

  useEffect(() => {
    if (activePriceSegmentationsData && activePriceSegmentationsData.activePriceSegmentations) {
      setPriceSegmentations(prevSegmentations => {
        const uniqueSegmentations = [...prevSegmentations];
        activePriceSegmentationsData.activePriceSegmentations.forEach(segmentation => {
          if (!uniqueSegmentations.some(s => s.id === segmentation.id)) {
            uniqueSegmentations.push(segmentation);
          }
        });
        return uniqueSegmentations;
      });
    }
  }, [activePriceSegmentationsData]);

  useEffect(() => {
    if (initialValues) {
      console.log('Initializing form with values:', initialValues);
      
      if(initialValues.group){
        setGroups([{id: initialValues.group.id, name: initialValues.group.name}]);
      }
      
      let infosData = [];
      if (initialValues.infos) {
        try {
          // Handle string to JSON parsing if needed
          if (typeof initialValues.infos === 'string') {
            infosData = JSON.parse(initialValues.infos);
          } else {
            infosData = initialValues.infos;
          }
          
          console.log('Original infos data:', infosData);
          
          // Ensure we have the places data from initialValues available in the places state
          const placeObjectsFromInfos = infosData
            .filter(info => info.placeData)
            .map(info => info.placeData)
            .filter(Boolean);
          
          if (placeObjectsFromInfos.length > 0) {
            console.log('Found place objects in infos data:', placeObjectsFromInfos);
            setPlaces(prevPlaces => {
              const uniquePlaces = [...prevPlaces];
              placeObjectsFromInfos.forEach(place => {
                if (!uniquePlaces.some(p => p.internalId === place.internalId)) {
                  uniquePlaces.push(place);
                }
              });
              return uniquePlaces;
            });
          }
          
          // Map to the expected format
          infosData = infosData.map(info => {
            console.log('Processing info:', info);
            console.log('Info place before processing:', info.place);
            
            return {
              ...info,
              place: typeof info.place === 'object' ? info.place.internalId?.toString() : info.place?.toString(),
              baseUnit: info.baseUnit || info.base_unit,
              quantityBasicUnities: info.quantityBasicUnities || info.quantity_basic_unities,
            };
          });
          
          console.log('Processed infos data:', infosData);
        } catch (e) {
          console.error('Error parsing infos:', e);
          infosData = [];
        }
      }
      
      let priceSegmentationsData = [];
      if (initialValues.priceSegmentations) {
        try {
          // Handle string to JSON parsing if needed
          if (typeof initialValues.priceSegmentations === 'string') {
            priceSegmentationsData = JSON.parse(initialValues.priceSegmentations);
          } else {
            priceSegmentationsData = initialValues.priceSegmentations;
          }
          
          // Map to the expected format
          priceSegmentationsData = priceSegmentationsData.map(segmentation => ({
            ...segmentation,
            priceSegmentationId: segmentation.priceSegmentationId?.toString() || segmentation.price_segmentation_id?.toString(),
            costValue: segmentation.costValue || segmentation.cost_value,
            minimumValue: segmentation.minimumValue || segmentation.minimum_value,
            value: segmentation.value,
            multiplier: segmentation.multiplier,
          }));
        } catch (e) {
          console.error('Error parsing priceSegmentations:', e);
          priceSegmentationsData = [];
        }
      }
      
      // Ensure we have at least one entry for each list if none exists
      if (infosData.length === 0) {
        infosData = [{}];
      }
      
      if (priceSegmentationsData.length === 0) {
        priceSegmentationsData = [{}];
      }
      
      const formValues = {
        ...initialValues,
        group: initialValues.group?.id,
        infos: infosData,
        priceSegmentations: priceSegmentationsData,
      };
      
      console.log('Setting form values:', formValues);
      form.setFieldsValue(formValues);
      
      // Set fileList if product has an image
      if (initialValues.imageUrl) {
        setFileList([{
          uid: '-1',
          name: 'Imagem do produto',
          status: 'done',
          url: initialValues.imageUrl,
        }]);
      } else {
        setFileList([]);
      }
    } else {
      form.resetFields();
      setFileList([]);
    }
  }, [initialValues, form, priceSegmentations]);

  const handleSearchGroup = (value) => {
    setSearchTerm(value);
  };

  const handleSearchPriceSegmentation = (value) => {
    if (value.length > 2 || value === '*') {
      searchPriceSegmentations({ variables: { query: value } });
    }
  };

  const handleSearchPlace = (value) => {
    if (value.length > 2 || value === '*') {
      searchPlaces({ variables: { query: value } });
    }
  };

  const handlePlaceChange = (value, option) => {
    console.log('Place selected:', value);
    console.log('Place option data:', option);
  };

  const handleChange = ({ fileList }) => {
    // Make sure only the latest file is kept
    setFileList(fileList.slice(-1));
  };

  const handleSubmit = async (values) => {
    if (isSubmittingRef.current) return;

    isSubmittingRef.current = true;
    setIsSubmitting(true);

    console.log('Raw form values before submission:', values);
    
    const formattedValues = {
      ...values,
      infos: values.infos.map(info => ({
        place: info.place,
        baseUnit: info.baseUnit,
        quantityBasicUnities: info.quantityBasicUnities,
        un: info.un,
        stock: (info.stock || '0').toString(),
        width: info.width,
        height: info.height,
        weight: info.weight,
      })),
      priceSegmentations: values.priceSegmentations.map(segmentation => ({
        priceSegmentationId: segmentation.priceSegmentationId,
        costValue: segmentation.costValue,
        minimumValue: segmentation.minimumValue,
        value: segmentation.value,
        multiplier: segmentation.multiplier,
        un: segmentation.un,
      })),
      groupId: values.group,
    };

    if (fileList.length > 0 && fileList[0].originFileObj) {
      formattedValues.image = fileList[0].originFileObj;
    }

    console.log('Submitting product data:', formattedValues);

    try {
      if (isEditMode) {
        console.log('Updating product with ID:', initialValues.id);
        await updateProduct({
          variables: {attributes: {id: initialValues.id, ...formattedValues}},
          update: (cache, { data: { updatePedidosMoveisProduct } }) => {
            console.log('Update response:', updatePedidosMoveisProduct);
            const { product } = updatePedidosMoveisProduct;
            cache.modify({
              id: cache.identify(product),
              fields: {
                reference() { return product.reference; },
                name() { return product.name; },
                brand() { return product.brand; },
                group() { return product.group; },
                description() { return product.description; },
                imageUrl() { return product.imageUrl; },
              }
            });
          }
        });
      } else {
        console.log('Creating new product');
        await createProduct({
          variables: {attributes: formattedValues},
          update: (cache, { data: { createPedidosMoveisProduct } }) => {
            console.log('Create response:', createPedidosMoveisProduct);
            const { product } = createPedidosMoveisProduct;
            cache.modify({
              fields: {
                products(existingProducts = { edges: [] }) {
                  const newProductRef = cache.writeFragment({
                    data: product,
                    fragment: gql`
                      fragment NewProduct on Product {
                        id
                        reference
                        name
                        brand
                        description
                        group {
                          id
                          name
                        }
                        imageUrl
                      }
                    `
                  });
                  return {
                    ...existingProducts,
                    edges: [...existingProducts.edges, { node: newProductRef }],
                  };
                }
              }
            });
          }
        });
      }
      refetchProducts(); // Refetch products to update the list
    } catch (error) {
      console.error('Error submitting product:', error);
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
      form.resetFields();
      onCancel();
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  }

  return (
    <Modal
      title={isEditMode ? "Editar Produto" : "Criar Produto"}
      open={visible}
      onCancel={handleCancel}
      footer={null}
      maskClosable={false}
      getContainer={false}
      width={1200} 
    >
      <Form
        form={form}
        initialValues={initialValues}
        onFinish={handleSubmit}
        className="custom-form"
        layout="vertical"
        style={{ width: '100%' }}
      >
        <Row gutter={16}>
          <Col span={6}>
            <Form.Item name="reference" label="Referência" rules={[{ required: true, message: 'Por favor insira a referência' }]}>
              <Input />
            </Form.Item>
          </Col>
          <Col span={18}>
            <Form.Item name="name" label="Nome" rules={[{ required: true, message: 'Por favor insira o nome' }]}>
              <Input />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="brand" label="Marca" rules={[{ required: true, message: 'Por favor insira a marca' }]}>
              <Input />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="group" label="Grupo">
              <Select
                showSearch
                placeholder="Pesquisar grupo"
                onSearch={handleSearchGroup}
                filterOption={false}
                options={groups.map(group => ({ value: group.id, label: group.name }))}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={24}>
            <Form.Item name="description" label="Descrição" >
              <Input.TextArea />
            </Form.Item>
          </Col>
        </Row>

        <h3>Tabelas de Preço</h3>
        <Form.List name="priceSegmentations" initialValue={[{}]}>
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name, ...restField }) => (
                <Row gutter={16} key={key}>
                  <Col span={6}>
                    <Form.Item
                      {...restField}
                      name={[name, 'priceSegmentationId']}
                      label="Tabela de Preço"
                      rules={[{ required: true, message: 'Tabela de preço é obrigatória' }]}
                    >
                      <Select
                        showSearch
                        placeholder="Pesquisar Tabela de preço"
                        onSearch={handleSearchPriceSegmentation}
                        filterOption={false}
                        options={priceSegmentations.map(priceSegmentation => ({
                          label: priceSegmentation.name,
                          value: priceSegmentation.id.toString(),
                        }))}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={2}>
                    <Form.Item
                      {...restField}
                      name={[name, 'un']}
                      label="UN"
                      rules={[{ required: true, message: 'UN é obrigatória' }]}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col span={3}>
                    <Form.Item
                      {...restField}
                      name={[name, 'costValue']}
                      label="Custo"
                      rules={[{ required: true, message: 'Custo é obrigatório' }]}
                    >
                      <CurrencyInput />
                    </Form.Item>
                  </Col>
                  <Col span={3}>
                    <Form.Item
                      {...restField}
                      name={[name, 'value']}
                      label="Valor"
                      rules={[{ required: true, message: 'Valor é obrigatório' }]}
                    >
                      <CurrencyInput />
                    </Form.Item>
                  </Col>
                  <Col span={4}>
                    <Form.Item
                      {...restField}
                      name={[name, 'minimumValue']}
                      label="Valor Mínimo"
                      rules={[{ required: true, message: 'Valor Mínimo é obrigatório' }]}
                    >
                      <CurrencyInput />
                    </Form.Item>
                  </Col>
                  <Col span={4}>
                    <Form.Item
                      {...restField}
                      name={[name, 'multiplier']}
                      label="Multiplicador"
                      rules={[{ required: true, message: 'Multiplicador é obrigatório' }]}
                    >
                      <InputNumber style={{ width: '100%' }} min={1} />
                    </Form.Item>
                  </Col>
                  <Col span={2} style={{ textAlign: 'center', paddingTop: '25px' }}>
                    {fields.length > 1 && (
                      <Button
                        type="text"
                        icon={<CloseCircleOutlined style={{ color: 'red' }} />}
                        onClick={() => remove(name)}
                      />
                    )}
                  </Col>
                </Row>
              ))}
              <Form.Item>
                <Button type="dashed" onClick={() => add()} style={{ width: '100%', marginBottom: 16 }} icon={<PlusOutlined />}>
                  Adicionar Tabela de preço
                </Button>
              </Form.Item>
            </>
          )}
        </Form.List>
        
        <h3>Informações Adicionais</h3>
        <Form.List name="infos" initialValue={[{}]}>
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name, ...restField }) => (
                <Row gutter={16} key={key}>
                  <Col span={22}>
                    <Row gutter={16}>
                      <Col span={6}>
                        <Form.Item
                          {...restField}
                          name={[name, 'place']}
                          label="Empresa"
                          rules={[{ required: true, message: 'Empresa é obrigatória' }]}
                        >
                          <Select
                            showSearch
                            placeholder="Pesquisar empresa"
                            onSearch={handleSearchPlace}
                            onChange={handlePlaceChange}
                            filterOption={(input, option) => 
                              option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
                            }
                            options={places.map(place => ({
                              label: place.corporateName || place.name || 'Empresa sem nome',
                              value: place.internalId.toString(),
                            }))}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={6}>
                        <Form.Item
                          {...restField}
                          name={[name, 'un']}
                          label="UN"
                          rules={[{ required: true, message: 'UN é obrigatório' }]}
                        >
                          <Input />
                        </Form.Item>
                      </Col>
                      <Col span={6}>
                        <Form.Item
                          {...restField}
                          name={[name, 'stock']}
                          label="Estoque"
                          rules={[{ required: true, message: 'Estoque é obrigatório' }]}
                        >
                          <InputNumber
                            style={{ width: '100%' }}
                            min={0}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={6}>
                        <Form.Item
                          {...restField}
                          name={[name, 'baseUnit']}
                          label="Unidade Base"
                          rules={[{ required: true, message: 'Unidade Base é obrigatória' }]}
                        >
                          <Select
                            options={[
                              { label: 'Sim', value: true },
                              { label: 'Não', value: false },
                            ]}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row gutter={16}>
                      <Col span={6}>
                        <Form.Item {...restField} name={[name, 'width']} label="Largura (cm)">
                          <Input />
                        </Form.Item>
                      </Col>
                      <Col span={6}>
                        <Form.Item {...restField} name={[name, 'height']} label="Altura (cm)">
                          <Input />
                        </Form.Item>
                      </Col>
                      <Col span={6}>
                        <Form.Item {...restField} name={[name, 'weight']} label="Peso (kg)">
                          <Input />
                        </Form.Item>
                      </Col>
                      <Col span={6}>
                        <Form.Item
                          {...restField}
                          name={[name, 'quantityBasicUnities']}
                          label="Qtd de Unidades Básicas"
                          rules={[{ required: true, message: 'Qtd de Unidades Básicas é obrigatória' }]}
                        >
                          <InputNumber style={{ width: '100%' }} min={1} />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Col>
                  <Col span={2} style={{ textAlign: 'center', paddingTop: '57px' }}>
                    {fields.length > 1 && (
                      <Button
                        type="text"
                        icon={<CloseCircleOutlined style={{ color: 'red' }} />}
                        onClick={() => remove(name)}
                      />
                    )}
                  </Col>
                </Row>
              ))}
              <Form.Item>
                <Button type="dashed" onClick={() => add()} style={{ width: '100%', marginBottom: 16 }} icon={<PlusOutlined />}>
                  Adicionar Informação
                </Button>
              </Form.Item>
            </>
          )}
        </Form.List>

        <Row gutter={16}>
          <Col span={24}>
            <Form.Item name="image" label="Imagem">
              <Upload
                listType="picture"
                fileList={fileList}
                beforeUpload={() => false} // Prevent automatic upload
                onChange={handleChange}
                maxCount={1} // Limit to one file only
                onRemove={() => setFileList([])}
              >
                <Button icon={<UploadOutlined />}>Selecionar Imagem</Button>
              </Upload>
            </Form.Item>
          </Col>
        </Row>

        <Row justify="end" gutter={16}>
          <Col>
            <Button onClick={handleCancel} disabled={isSubmitting}>
              Cancelar
            </Button>
          </Col>
          <Col>
            <Button type="primary" htmlType="submit" loading={isSubmitting} disabled={isSubmitting}>
              {isEditMode ? "Atualizar" : "Criar"}
            </Button>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default ProductModalForm;
